import React, { useState } from 'react';
import './LandingPage.css';

const NGK_PROFILE_URL = 'https://www.nexgenklick.ai/landing';
const SV4U_URL = 'https://www.siliconvalley4u.com';
const NGK_RESOURCES_URL = 'https://www.nexgenklick.ai/resources';
const NGK_SCHOOLS_URL = 'https://www.nexgenklick.ai/schools';

function LandingPage({ onSelectK12, onSelectAdults }) {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email.trim()) setSubscribed(true);
  };

  const scrollTo = (id) => {
    setMenuOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="hub-root">

      {/* ═══════════ NAV ═══════════ */}
      <nav className="hub-nav">
        <div className="hub-nav-inner">
          <a className="hub-nav-logo" href="#home" onClick={e => { e.preventDefault(); scrollTo('home'); }}>
            <img src="/logos/ngk-logo.png" alt="NexGenKlick" className="hub-nav-logo-img" />
          </a>

          <div className={`hub-nav-links ${menuOpen ? 'open' : ''}`}>
            <button className="hub-nav-link" onClick={() => scrollTo('home')}>Home</button>
            <button className="hub-nav-link" onClick={() => scrollTo('programs')}>Programs</button>
            <button className="hub-nav-link" onClick={() => scrollTo('resources')}>Learning Resources</button>
            <button className="hub-nav-link" onClick={() => scrollTo('gamified')}>Gamified Learning</button>
            <button className="hub-nav-link" onClick={() => scrollTo('profiles')}>Profiles</button>
            <button className="hub-nav-link" onClick={() => scrollTo('updates')}>News & Updates</button>
            <button className="hub-nav-link" onClick={() => scrollTo('schools')}>For Schools</button>
          </div>

          <a className="hub-nav-cta" href={NGK_PROFILE_URL} target="_blank" rel="noopener noreferrer">
            Create Profile
          </a>

          <button className="hub-hamburger" onClick={() => setMenuOpen(m => !m)} aria-label="Toggle menu">
            <span /><span /><span />
          </button>
        </div>
      </nav>

      {/* ═══════════ HERO ═══════════ */}
      <section id="home" className="hub-hero">
        <div className="hub-hero-inner">
          <div className="hub-hero-eyebrow">Central Learning Hub · NexGenKlick Ecosystem</div>
          <h1>
            Learn. Build Skills.<br />
            Stay <span>Future-Ready.</span>
          </h1>
          <p className="hub-hero-sub">
            Explore technology programs, access learning resources, and build your
            verified digital learning profile with NexGenKlick.
          </p>
          <div className="hub-hero-ctas">
            <button className="hub-btn-primary" onClick={onSelectK12}>
              🚀 Explore K-12 Programs
            </button>
            <button className="hub-btn-secondary" onClick={onSelectAdults}>
              Explore Lifelong Learning →
            </button>
          </div>
          <div className="hub-hero-secondary-cta">
            <a className="hub-link-ghost" href={NGK_PROFILE_URL} target="_blank" rel="noopener noreferrer">
              Create NexGenKlick Profile →
            </a>
          </div>
        </div>
      </section>

      {/* ═══════════ STATS BAR ═══════════ */}
      <div className="hub-stats-bar">
        <div className="hub-stats-inner">
          <div className="hub-stat-item"><strong>12</strong> Program Types</div>
          <div className="hub-stat-divider" />
          <div className="hub-stat-item"><strong>3</strong> Bay Area Locations</div>
          <div className="hub-stat-divider" />
          <div className="hub-stat-item"><strong>2</strong> Adult Learning Courses</div>
          <div className="hub-stat-divider" />
          <div className="hub-stat-item"><strong>40+</strong> Learning Resources</div>
          <div className="hub-stat-divider" />
          <div className="hub-stat-item"><strong>Ages 6–18</strong> &amp; <strong>Adults</strong> Welcome</div>
        </div>
      </div>

      {/* ═══════════ HOW IT WORKS ═══════════ */}
      <div className="hub-how-strip">
        <div className="hub-how-inner">
          <p className="hub-how-label">How It Works</p>
          <div className="hub-how-steps">
            <div className="hub-how-step">
              <div className="hub-how-num">1</div>
              <div className="hub-how-text">
                <strong>Browse SiliconValley4U Programs</strong>
                <span>Find a K-12 program or adult class at siliconvalley4u.com</span>
              </div>
            </div>
            <div className="hub-how-arrow">→</div>
            <div className="hub-how-step">
              <div className="hub-how-num">2</div>
              <div className="hub-how-text">
                <strong>Register &amp; Create NexGenKlick Profile</strong>
                <span>Sign up for NexGenKlick during your SV4U registration</span>
              </div>
            </div>
            <div className="hub-how-arrow">→</div>
            <div className="hub-how-step">
              <div className="hub-how-num">3</div>
              <div className="hub-how-text">
                <strong>Unlock Gamified Content</strong>
                <span>Access challenges, missions, badges, and your learning dashboard</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════ CHOOSE YOUR LEARNING PATH ═══════════ */}
      <section id="programs">
        <div className="hub-section">
          <p className="hub-label">Our Programs</p>
          <h2 className="hub-title">Choose Your Learning Path</h2>
          <p className="hub-subtitle">
            Two audiences, one mission — empower every learner with the digital
            skills they need to thrive.
          </p>

          <div className="hub-path-grid">
            {/* K-12 */}
            <div className="hub-path-card hub-path-card-k12" onClick={onSelectK12}>
              <span className="hub-path-badge hub-path-badge-blue">🎮 Gamified Learning</span>
              <h3>K-12 Technology Learning</h3>
              <p>
                Hands-on programs where students earn XP, unlock achievements, and build
                real projects — from beginner coding to advanced AI and robotics.
              </p>
              <div className="hub-path-tags">
                {['AI & Python', 'Roblox Game Design', 'Robotics & Coding', 'Web Development', '3D Modeling'].map(t => (
                  <span key={t} className="hub-tag">{t}</span>
                ))}
              </div>
              <div className="hub-path-features">
                {[
                  'Project-based learning',
                  'Gamified learning modules',
                  'Certificates and digital portfolios',
                ].map(f => (
                  <div key={f} className="hub-path-feature">
                    <div className="hub-feature-dot hub-feature-dot-blue" />
                    {f}
                  </div>
                ))}
              </div>
              <div className="hub-bonus-callout hub-bonus-callout-blue">
                <span className="hub-bonus-callout-icon">🎮</span>
                <p><strong>Bonus:</strong> Register for a SV4U program and sign up for NexGenKlick to unlock gamified content, missions, and your digital learning dashboard — free.</p>
              </div>
              <button className="hub-path-cta hub-path-cta-blue">
                Explore K-12 Programs 🚀
              </button>
            </div>

            {/* Adults */}
            <div className="hub-path-card hub-path-card-adults" onClick={onSelectAdults}>
              <span className="hub-path-badge hub-path-badge-green">✓ Open to Adult Learners</span>
              <h3>Lifelong Learning Programs</h3>
              <p>
                Practical, friendly programs designed for adult learners and professionals —
                building confidence with technology, finance, and wellness.
              </p>
              <div className="hub-path-tags">
                {['Digital & Financial Life Skills', 'Computer Basics', 'Modern Technology', 'AI for Everyday Use', 'Cognitive Wellness'].map(t => (
                  <span key={t} className="hub-tag">{t}</span>
                ))}
              </div>
              <div className="hub-path-features">
                {[
                  'Practical real-world skills',
                  'Friendly learning environment',
                  'Community learning',
                ].map(f => (
                  <div key={f} className="hub-path-feature">
                    <div className="hub-feature-dot hub-feature-dot-green" />
                    {f}
                  </div>
                ))}
              </div>
              <div className="hub-bonus-callout hub-bonus-callout-green">
                <span className="hub-bonus-callout-icon">📈</span>
                <p><strong>Bonus:</strong> Enroll in an adult course at SiliconValley4U and create your NexGenKlick profile to track your progress, earn certificates, and stay motivated — free.</p>
              </div>
              <button className="hub-path-cta hub-path-cta-green">
                Explore Lifelong Learning Programs →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ LEARNING RESOURCES ═══════════ */}
      <section id="resources" className="hub-section-alt">
        <div className="hub-section hub-text-center">
          <p className="hub-label">Hosted on NexGenKlick</p>
          <h2 className="hub-title">Learning Resources &amp; Content</h2>
          <p className="hub-subtitle">
            Access guides, tutorials, and practical materials covering AI, technology,
            financial literacy, and more.
          </p>

          <div className="hub-resources-grid">
            {[
              { icon: '🤖', title: 'AI Learning Resources', desc: 'Intro guides, hands-on projects, and tutorials on artificial intelligence.' },
              { icon: '💻', title: 'Technology Skills', desc: 'Coding fundamentals, development tools, and digital literacy resources.' },
              { icon: '💰', title: 'Financial & Digital Literacy', desc: 'Modern digital tools and financial technology concepts for everyday use.' },
              { icon: '📚', title: 'Learning Guides & Articles', desc: 'Educational resources and practical guides across all skill levels.' },
            ].map(r => (
              <div key={r.title} className="hub-resource-card">
                <div className="hub-resource-icon">{r.icon}</div>
                <h4>{r.title}</h4>
                <p>{r.desc}</p>
              </div>
            ))}
          </div>

          <div className="hub-center-cta">
            <a className="hub-btn-outline" href={NGK_RESOURCES_URL} target="_blank" rel="noopener noreferrer">
              Explore Learning Resources →
            </a>
          </div>
        </div>
      </section>

      {/* ═══════════ GAMIFIED LEARNING ═══════════ */}
      <section id="gamified" className="hub-section-dark">
        <div className="hub-section hub-text-center">
          <p className="hub-label" style={{ color: '#4ade80' }}>Key Differentiator</p>
          <h2 className="hub-title hub-title-white">Gamified Learning</h2>
          <p className="hub-subtitle hub-subtitle-white">
            When you register for a SiliconValley4U class or program and sign up for
            NexGenKlick, you unlock interactive challenges, learning missions, and
            a gamified dashboard that makes skills stick.
          </p>

          <div className="hub-gamif-grid">
            {[
              { icon: '🤖', title: 'AI Explorer Challenges', desc: 'Build chatbots and simple AI models through guided missions and interactive challenges.' },
              { icon: '⚡', title: 'Coding Missions', desc: 'Solve coding puzzles, complete mini-projects, and earn points for every milestone.' },
              { icon: '🏅', title: 'Skill Badges & Portfolio', desc: 'Earn verified badges for completing modules and showcase projects in your NexGenKlick profile.' },
            ].map(c => (
              <div key={c.title} className="hub-gamif-card">
                <div className="hub-gamif-icon">{c.icon}</div>
                <h4>{c.title}</h4>
                <p>{c.desc}</p>
              </div>
            ))}
          </div>

          {/* Demo dashboard preview */}
          <div className="hub-demo-box">
            <div className="hub-demo-label">✨ Preview — Your Learning Dashboard</div>
            <div className="hub-demo-player">
              <div className="hub-demo-avatar">A</div>
              <div>
                <div className="hub-demo-player-name">AceCoderX</div>
                <div className="hub-demo-player-level">Level 4 Coder</div>
              </div>
            </div>
            <div className="hub-demo-xp-row">
              <span>240 XP</span>
              <span>Level 5 at 360 XP</span>
            </div>
            <div className="hub-xp-bar">
              <div className="hub-xp-fill" />
            </div>
            <div className="hub-demo-stats">
              <div className="hub-demo-stat">
                <span className="hub-demo-stat-val">3/5</span>
                <span className="hub-demo-stat-lbl">Topics Done</span>
              </div>
              <div className="hub-demo-stat">
                <span className="hub-demo-stat-val">87%</span>
                <span className="hub-demo-stat-lbl">Accuracy</span>
              </div>
              <div className="hub-demo-stat">
                <span className="hub-demo-stat-val">🔥 5</span>
                <span className="hub-demo-stat-lbl">Day Streak</span>
              </div>
            </div>
          </div>

          <div className="hub-gamif-cta-wrap">
            <a className="hub-btn-green" href={SV4U_URL} target="_blank" rel="noopener noreferrer">
              Register for a SV4U Program &amp; Unlock Gamified Content →
            </a>
          </div>
        </div>
      </section>

      {/* ═══════════ DIGITAL LEARNING PROFILE ═══════════ */}
      <section id="profiles">
        <div className="hub-section">
          <p className="hub-label">Powered by NexGenKlick</p>
          <h2 className="hub-title">Build Your Digital Learning Profile</h2>
          <p className="hub-subtitle">
            A verified profile that grows with you — tracking skills, achievements,
            and program participation in one place.
          </p>

          <div className="hub-profile-wrap">
            <div className="hub-profile-grid">
              {[
                {
                  icon: '🎓',
                  audience: 'For Students',
                  desc: 'Build a digital portfolio that showcases your coding and AI projects to schools and beyond.',
                  items: ['Skills & certifications', 'Project showcase', 'Achievement badges', 'Program participation'],
                },
                {
                  icon: '👔',
                  audience: 'For Adult Learners',
                  desc: 'Track your learning progress and demonstrate new technology skills to employers or community.',
                  items: ['Learning progress tracker', 'Verified activities', 'Course completions', 'Community engagement'],
                },
                {
                  icon: '🏫',
                  audience: 'For Schools',
                  desc: 'Showcase your programs and help students build verified portfolios that reflect their achievements.',
                  items: ['Program listing', 'Student achievement tracking', 'Course materials sharing', 'Resource hub access'],
                },
              ].map(col => (
                <div key={col.audience} className="hub-profile-col">
                  <div className="hub-profile-col-icon">{col.icon}</div>
                  <h4>{col.audience}</h4>
                  <p>{col.desc}</p>
                  <div className="hub-profile-items">
                    {col.items.map(item => (
                      <div key={item} className="hub-profile-item">
                        <div className="hub-profile-check">✓</div>
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              <div className="hub-profile-cta">
                <a className="hub-btn-outline" href={NGK_PROFILE_URL} target="_blank" rel="noopener noreferrer">
                  Create Your Free Profile →
                </a>
              </div>
            </div>

            {/* Explore NexGenKlick panel */}
            <div className="hub-explore-ngk">
              <div className="hub-explore-ngk-icon">🌐</div>
              <h4>Explore NexGenKlick</h4>
              <p>
                NexGenKlick is more than a profile — it's a full learning platform with
                resources, gamified content, community tools, and verified achievement tracking.
              </p>
              <a href="https://www.nexgenklick.ai" target="_blank" rel="noopener noreferrer">
                Explore NexGenKlick →
              </a>
            </div>

            {/* Profile preview mockup */}
            <div className="hub-profile-preview">
              <div className="hub-preview-label">Sample NexGenKlick Profile</div>
              <div className="hub-preview-header">
                <div className="hub-preview-avatar">S</div>
                <div>
                  <div className="hub-preview-name">Sarah K.</div>
                  <div className="hub-preview-handle">@sarahcodes · Level 7</div>
                </div>
              </div>
              <div className="hub-preview-badges">
                {['AI Explorer', 'Python Pro', 'Web Builder', 'Quiz Master', 'Perfect Score'].map(b => (
                  <span key={b} className="hub-preview-badge">{b}</span>
                ))}
              </div>
              <div className="hub-preview-stats">
                <div className="hub-preview-stat">
                  <strong>12</strong>
                  <span>Projects</span>
                </div>
                <div className="hub-preview-stat">
                  <strong>8</strong>
                  <span>Badges</span>
                </div>
                <div className="hub-preview-stat">
                  <strong>94%</strong>
                  <span>Accuracy</span>
                </div>
                <div className="hub-preview-stat">
                  <strong>🔥 21</strong>
                  <span>Streak</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ PARTNER PROGRAMS ═══════════ */}
      <section className="hub-section-alt">
        <div className="hub-section">
          <p className="hub-label">Our Program Partner</p>
          <h2 className="hub-title">Powered by SiliconValley4U</h2>
          <p className="hub-subtitle">
            All K-12 and adult programs are delivered by SiliconValley4U —
            a proven Bay Area technology education provider.
          </p>

          <div className="hub-partner-card">
            <div className="hub-partner-logo">
              <img src="/logos/sv4u-logo.png" alt="SiliconValley4U" className="hub-partner-logo-img" />
            </div>
            <div className="hub-partner-body">
              <h3>SiliconValley4U</h3>
              <p>
                Hands-on technology education programs for K-12 students and adult learners
                across the Bay Area. Combining project-based learning with gamification to
                build real, future-ready skills.
              </p>
              <div className="hub-partner-cats">
                {['AI & Machine Learning', 'Coding & Python', 'Robotics', 'Game Design', 'Digital Skills'].map(c => (
                  <span key={c} className="hub-tag">{c}</span>
                ))}
              </div>
              <div className="hub-partner-locations">
                {['📍 Milpitas', '📍 Pleasanton', '📍 San Mateo'].map(l => (
                  <span key={l} className="hub-location-chip">{l}</span>
                ))}
              </div>
              <div className="hub-dual-cta">
                <a className="hub-btn-outline" href={SV4U_URL} target="_blank" rel="noopener noreferrer">
                  Browse Programs →
                </a>
                <a className="hub-btn-outline hub-btn-outline-green" href={SV4U_URL} target="_blank" rel="noopener noreferrer">
                  🎮 Register &amp; Get NexGenKlick Access
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ NEWSLETTER ═══════════ */}
      <section id="updates">
        <div className="hub-section hub-text-center">
          <p className="hub-label">Stay Connected</p>
          <h2 className="hub-title">Stay Updated</h2>
          <p className="hub-subtitle">
            Get the latest on new programs, learning resources, tech education insights,
            and community events.
          </p>

          <div className="hub-newsletter-grid">
            {[
              { icon: '📚', title: 'Learning Updates', desc: 'New programs, courses, and resources added to the platform.' },
              { icon: '🔬', title: 'Tech Education Insights', desc: 'Articles on AI, technology trends, and learning strategies.' },
              { icon: '🗓️', title: 'Program Announcements', desc: 'Upcoming programs, classes, and enrollment dates.' },
              { icon: '🤝', title: 'Community News', desc: 'Events, workshops, and new school partnerships.' },
            ].map(n => (
              <div key={n.title} className="hub-newsletter-card">
                <div className="hub-newsletter-card-icon">{n.icon}</div>
                <h4>{n.title}</h4>
                <p>{n.desc}</p>
              </div>
            ))}
          </div>

          <div className="hub-newsletter-form-wrap">
            {subscribed ? (
              <p className="hub-success">✅ You're subscribed! We'll be in touch soon.</p>
            ) : (
              <form className="hub-newsletter-form" onSubmit={handleSubscribe}>
                <input
                  type="email"
                  className="hub-newsletter-input"
                  placeholder="Your email address"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                />
                <button type="submit" className="hub-newsletter-btn">Subscribe</button>
              </form>
            )}
            <p className="hub-newsletter-fine">No spam. Unsubscribe anytime.</p>
          </div>
        </div>
      </section>

      {/* ═══════════ FOR SCHOOLS ═══════════ */}
      <section id="schools" className="hub-section-dark">
        <div className="hub-section hub-text-center">
          <p className="hub-label" style={{ color: '#4ade80' }}>Grow Together</p>
          <h2 className="hub-title hub-title-white">For Schools &amp; Organizations</h2>
          <p className="hub-subtitle hub-subtitle-white">
            Join the NexGenKlick ecosystem to share resources, showcase programs,
            and help students build verified digital portfolios.
          </p>

          <div className="hub-schools-grid">
            {[
              { icon: '📤', title: 'Share Learning Resources', desc: 'Upload and share course materials, guides, and educational content with your students.' },
              { icon: '🏆', title: 'Showcase Programs', desc: 'List your technology programs on the NexGenKlick learning hub.' },
              { icon: '📊', title: 'Track Student Achievements', desc: 'Monitor student progress, badge completions, and learning milestones.' },
              { icon: '📋', title: 'Provide Course Materials', desc: 'Integrate your curriculum with NexGenKlick\'s gamified learning modules.' },
            ].map(s => (
              <div key={s.title} className="hub-schools-card">
                <div className="hub-schools-card-icon">{s.icon}</div>
                <h4>{s.title}</h4>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>

          <a className="hub-btn-green" href={NGK_SCHOOLS_URL} target="_blank" rel="noopener noreferrer">
            Register Your School →
          </a>
        </div>
      </section>

      {/* ═══════════ FOOTER ═══════════ */}
      <footer className="hub-footer">
        <div className="hub-footer-inner">
          <div className="hub-footer-brand">
            <span className="hub-footer-brand-name">NexGenKlick <span>Learn</span></span>
            <span className="hub-footer-tagline">
              A central learning hub connecting programs, resources, and verified learner profiles.
            </span>
          </div>

          <div className="hub-footer-col">
            <h4>Programs</h4>
            <ul>
              <li><button onClick={onSelectK12}>K-12 Coding &amp; AI Programs</button></li>
              <li><button onClick={onSelectAdults}>Digital Skills for Adults</button></li>
              <li><a href={SV4U_URL} target="_blank" rel="noopener noreferrer">SiliconValley4U</a></li>
            </ul>
          </div>

          <div className="hub-footer-col">
            <h4>Platform</h4>
            <ul>
              <li><button onClick={() => scrollTo('resources')}>Learning Resources</button></li>
              <li><button onClick={() => scrollTo('gamified')}>Gamified Learning</button></li>
              <li><button onClick={() => scrollTo('profiles')}>Learner Profiles</button></li>
              <li><button onClick={() => scrollTo('schools')}>For Schools</button></li>
            </ul>
          </div>

          <div className="hub-footer-col">
            <h4>Contact</h4>
            <ul>
              <li>📞 (408) 505-5499</li>
              <li>✉ info@siliconvalley4u.com</li>
              <li>📍 Milpitas · Pleasanton · San Mateo</li>
            </ul>
          </div>
        </div>

        <div className="hub-footer-bottom">
          <span className="hub-footer-copy">
            © 2025 NexGenKlick / SiliconValley4U. All rights reserved.
          </span>
          <a
            href={NGK_PROFILE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hub-footer-ngk-btn"
          >
            🚀 Create NexGenKlick Profile
          </a>
        </div>
      </footer>

    </div>
  );
}

export default LandingPage;
